<template>
    <header 
        class="site-header" 
        :class="[
            theme ? `theme-${theme}` : '',
            pageClass,
            {
                'sticky': isSticky,
                'frosty': isSticky && !panelsOpen && isMainPage,
                'panel-frosty': isSticky && panelsOpen,
                'entering': isEntering
            }
        ]"
    >
       <div class="site-header-container">
            <router-link to="/" class="logo" v-if="shouldShowLogo">
                <Logo
                    :text-only="true"
                    width="150"
                    height="25"
                    :color="logoColor"
                />
            </router-link>
            <!-- 
            <div class="logo-panel">
                panel-open
            </div>
            -->
            <div class="navigation-wrapper">
                <Navigation />
            </div>
       </div>
    </header>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useEventListener } from '@vueuse/core'
import Navigation from '../Navigation.vue';
import Logo from '../Logo.vue';

export default {
    components: {
        Navigation,
        Logo
    },
    props: {
        theme: {
            type: String,
            default: 'xw-white'
        },
        panelsOpen: {
            type: Boolean,
            default: false
        },
        activePanel: {
            type: String,
            default: null
        },
        isEntering: {
            type: Boolean,
            default: false
        }
    },
    setup(props) {
        const route = useRoute()
        const isSticky = ref(false)
        const activePanelListener = ref(null)
        const activePanelElement = ref(null)
        
        let scrollFrameId = null
        let panelScrollFrameId = null
        
        // Throttled scroll handler using requestAnimationFrame
        const throttledHandleScroll = () => {
            // Gate with visibility state - stop when tab is backgrounded
            if (document.visibilityState === 'hidden') return
            
            if (scrollFrameId !== null) return // Already scheduled
            
            scrollFrameId = requestAnimationFrame(() => {
                isSticky.value = window.scrollY > 30
                scrollFrameId = null
            })
        }
        
        // Throttled panel scroll handler using requestAnimationFrame
        const throttledHandlePanelScroll = (event) => {
            // Gate with visibility state
            if (document.visibilityState === 'hidden') return
            
            if (panelScrollFrameId !== null) return // Already scheduled
            
            const element = event.target
            panelScrollFrameId = requestAnimationFrame(() => {
                isSticky.value = element.scrollTop > 100
                panelScrollFrameId = null
            })
        }
        
        // Clean up any existing panel scroll listener
        const cleanupPanelListener = () => {
            if (activePanelElement.value && activePanelListener.value) {
                activePanelElement.value.removeEventListener('scroll', activePanelListener.value)
                activePanelElement.value = null
                activePanelListener.value = null
            }
            
            // Cancel any pending frame requests
            if (panelScrollFrameId !== null) {
                cancelAnimationFrame(panelScrollFrameId)
                panelScrollFrameId = null
            }
        }
        
        // Add panel scroll listener with proper tracking
        const addPanelListener = (panelSelector, delay = 500) => {
            setTimeout(() => {
                // Double-check panel is still open and selector is still valid
                if (!props.panelsOpen) return
                
                const panelContent = document.querySelector(panelSelector)
                if (panelContent) {
                    cleanupPanelListener() // Clean up any existing listener first
                    
                    activePanelElement.value = panelContent
                    activePanelListener.value = throttledHandlePanelScroll
                    panelContent.addEventListener('scroll', throttledHandlePanelScroll, { passive: true })
                }
            }, delay)
        }
        
        onMounted(() => {
            
            // setTimeout(() => {
            //     // Check initial state
            //     throttledHandleScroll()
            // }, 50);

            // Check initial state
            throttledHandleScroll()
            
            // Add panel listener if panels are initially open
            if (props.panelsOpen && props.activePanel) {
                const panelSelector = props.activePanel === 'left' 
                    ? '.side-panel.left.active .panel-content'
                    : '.side-panel.right.active .panel-content'
                addPanelListener(panelSelector)
            }
        })
        
        // Use VueUse for automatic event listener cleanup
        useEventListener('scroll', throttledHandleScroll, { passive: true })
        
        onBeforeUnmount(() => {
            cleanupPanelListener()
            
            // Cancel any pending scroll frame requests
            if (scrollFrameId !== null) {
                cancelAnimationFrame(scrollFrameId)
                scrollFrameId = null
            }
        })
        
        const isMainPage = computed(() => {
            // Only show frosty effect on main content pages, not in panels
            const mainPages = ['/', '/about', '/contact', '/news']
            const currentPath = route.path
            return mainPages.includes(currentPath) || 
                   currentPath.startsWith('/news/')
        })
        
        const shouldShowLogo = computed(() => {
            // Show logo on all pages EXCEPT home and panel pages
            const currentPath = route.path
            return currentPath !== '/' && 
                   currentPath !== '/design' && 
                   currentPath !== '/technology'
        })
        
        const pageClass = computed(() => {
            const currentPath = route.path
            
            if (props.panelsOpen && props.activePanel) {
                if (props.activePanel === 'left') {
                    return 'header-design'
                }
                if (props.activePanel === 'right') {
                    return 'header-technology'
                }
            }
            
            // Direct panel URLs
            if (currentPath === '/design') {
                return 'header-design'
            }
            if (currentPath === '/technology') {
                return 'header-technology'
            }
            
            // Regular pages
            if (currentPath === '/') {
                return 'header-home'
            }
            if (currentPath === '/about') {
                return 'header-about'
            }
            if (currentPath === '/contact') {
                return 'header-contact'
            }
            if (currentPath === '/news') {
                return 'header-news-listing'
            }
            if (currentPath.startsWith('/news/')) {
                return 'header-news-single'
            }
            
            // Fallback
            return 'header-unknown'
        })
        
        // Watch for panels opening/closing and switching to manage scroll listeners
        watch([() => props.panelsOpen, () => props.activePanel], ([panelsOpen, activePanel]) => {
            // Always clean up existing listeners first
            cleanupPanelListener()
            
            if (panelsOpen && activePanel) {
                // Add new panel scroll listener - target the correct active panel
                const panelSelector = activePanel === 'left' 
                    ? '.side-panel.left.active .panel-content'
                    : '.side-panel.right.active .panel-content'
                
                addPanelListener(panelSelector)
            } else {
                // Reset sticky state when panels close
                throttledHandleScroll()
            }
        })
        
        return {
            isSticky,
            isMainPage,
            shouldShowLogo,
            pageClass
        }
    },
    computed: {
        logoColor() { 
            // Map theme to logo color
            const themeMap = {
                'xw-white': 'black',
                'xw-yellow': 'black',
                'xw-grey': 'black',
                'xw-teal': 'white'
            };
            // Default to 'teal' if not matched
            return themeMap[this.theme] || 'teal';
        }
    }
}
</script>

<style lang="scss" scoped>
    @use '../../assets/scss/variables' as *;
    @use '../../assets/scss/container' as *;
    @use '../../assets/scss/mixins' as m;


   
.site-header {
    z-index: 109;
    margin-top: 0px;
    position: fixed;
    top: 0%;
    left: 8%;
    right: 8%;
    transition: all 0.1s ease;
    padding-bottom: 10px;
    width: 100%;
    left: 0;
    right: 0;
    z-index:90;

    &.header-home{
        @include m.below-tablet-portrait {
            display: none!important;
        }
    }
    
    // Smooth entrance animation
    &.entering {
        animation: headerFadeIn 0.6s ease-out;
    }

    
    &.header-design, &.header-technology{
        width: calc(100% - 153px);
        z-index: 109;
    }
    &.header-design{
        left:0!important;
    }
    &.header-technology{
        width: calc(100% - 153px);
        right: 0!important;
         left: 153px;
    }
    &.header-design.sticky, &.header-technology.sticky {
     
        z-index: 109;
        width: calc(100% - 153px);
        backdrop-filter: blur(18px);
        -webkit-backdrop-filter: blur(18px);
        background: color-mix(in srgb, var(--theme-bg) 80%, transparent);
        
    }

    &.header-design.sticky{
        left: 0;
    }
    &.header-technology.sticky {
        left: 153px;
    }

    &.header-design.sticky, &.header-technology.sticky{
        @include m.below-tablet-portrait {
            display: none;
        }
        translate: background-color .6s ease, height .6s ease;
        
        .panel-dragging &, .panel-opening &{
            background-color: transparent!important;
            border-bottom: none;
            //display: none;
            backdrop-filter: none;
             -webkit-backdrop-filter: none;
            background-color: transparent!important;
            height: 0;
        }
    }

    &.sticky {
        top: 0%;
        left: 8%;
        right: 8%;

        border-bottom: 1px var(--theme-border) solid;
       
        width: 84%;
        z-index:90;
        backdrop-filter: blur(18px);
        -webkit-backdrop-filter: blur(18px);
        background: color-mix(in srgb, var(--theme-bg) 80%, transparent);

        width: 100%;
        left: 0;
        right: 0;

        @include m.below-tablet-portrait {
            width: 100%;
            left: 0;
            right: 0;
            height: 0;
            background-color: var(--theme-bg)!important;

            &.header-home{
                display: none;
            }
        }

        .site-header-container {
            padding-top: 20px;
            padding-bottom: 5px;
    
            @include m.below-tablet-portrait {
              padding-bottom: 10px;
              border-bottom: 1px var(--theme-border) solid;
              background-color: var(--theme-bg);
            }
        }
    }
}
    
.site-header-container {
    @extend %container-header;
    padding-top: 20px;
    position: relative;
}

.logo {
    position: absolute;
    left: 0;
    //top: 30px;
    //transform: translateY(-50%);
    z-index: 10;
    width: 180px;
    height: 30px; 

    @include m.below-tablet-landscape {
        position: relative;
        margin: 0px auto 0;
        width: 200px;
        display: block;
       // border: 1px solid red;
    }
}

.navigation-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;

    @include m.below-tablet-landscape {
        display: none;
    }
}

// Header entrance animation
@keyframes headerFadeIn {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

</style>
